import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Spinner from '../spinner';
import Text from '../../containers/Text';
import { getEmailTrackingReport } from '../../api/requests/email-tracking-report';
import { getErrorMessage } from '../../shared/services/error-handler';

import {
  COLUMN_NAMES,
  FIELD_LABEL_KEYS,
  SERVER_ERROR_MESSAGE_KEYS,
  TRANSLATION_KEYS,
} from './constants';
import {
  buildRangeLabel,
  isValidDateRange,
  normalizeValue,
} from './helpers';

import './EmailTracking.scss';

const INITIAL_ERROR_STATE = { key: '', fallback: '' };
const INITIAL_RANGE = { from: '', to: '' };

const buildTableColumns = (emails) => {
  if (!Array.isArray(emails) || emails.length === 0) {
    return [];
  }

  const [firstEmail, ...restEmails] = emails;
  const firstColumns = Object.keys(firstEmail || {});

  return restEmails.reduce((columns, emailItem) => {
    Object.keys(emailItem || {}).forEach((columnName) => {
      if (!columns.includes(columnName)) {
        columns.push(columnName);
      }
    });

    return columns;
  }, [...firstColumns]);
};

const EmailReportTable = ({ emails, t, language }) => {
  const columns = buildTableColumns(emails);

  const tableWrapperRef = useRef(null);
  const dummyScrollRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const isSyncingRef = useRef(false);


  useEffect(() => {
    if (tableWrapperRef.current) {
      setScrollWidth(tableWrapperRef.current.scrollWidth);
    }
  }, [emails, columns]);

 
  const handleTableScroll = () => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    if (dummyScrollRef.current && tableWrapperRef.current) {
      dummyScrollRef.current.scrollLeft = tableWrapperRef.current.scrollLeft;
    }
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });
  };


  const handleDummyScroll = () => {
    if (isSyncingRef.current) return;
    isSyncingRef.current = true;
    if (tableWrapperRef.current && dummyScrollRef.current) {
      tableWrapperRef.current.scrollLeft = dummyScrollRef.current.scrollLeft;
    }
    requestAnimationFrame(() => {
      isSyncingRef.current = false;
    });
  };

  if (columns.length === 0) {
    return null;
  }

  return (
    <div className="email-report-table-outer">
      <div
        className="email-report-table-wrapper"
        ref={tableWrapperRef}
        onScroll={handleTableScroll}
      >
        <table className="email-report-table">
          <thead>
            <tr>
              <th className="table-index-column"></th>
              {columns.map((columnName) => {
                const labelKey = FIELD_LABEL_KEYS[columnName];
                const labelText = labelKey ? t(labelKey) : columnName;

                return (
                  <th key={columnName}>
                    <Text text={labelText} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {emails.map((emailItem, rowIndex) => (
              <tr key={`email-row-${rowIndex}`}>
                <td className="table-index-column">{rowIndex + 1}</td>
                {columns.map((columnName) => {
                  const value = normalizeValue({
                    value: emailItem[columnName],
                    columnName,
                    row: emailItem,
                    t,
                    language,
                  });

                  return (
                    <td key={`${rowIndex}-${columnName}`}>
                      <Text text={value} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

   
      <div
        className="sticky-dummy-scrollbar"
        ref={dummyScrollRef}
        onScroll={handleDummyScroll}
      >
        <div style={{ width: `${scrollWidth}px`, height: '1px' }} />
      </div>
    </div>
  );
};

const DateRangeInput = ({ id, labelKey, value, onChange, t, min, max }) => (
  <label className="date-input-label" htmlFor={id}>
    <Text text={t(labelKey)} />
    <input
      id={id}
      type="date"
      className="date-input"
      value={value}
      min={min}
      max={max}
      onChange={(event) => onChange(event.target.value)}
    />
  </label>
);

const EmailTrackingReport = () => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const [errorState, setErrorState] = useState(INITIAL_ERROR_STATE);
  const [emails, setEmails] = useState([]);
  const [range, setRange] = useState(INITIAL_RANGE);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showUnsentOnly, setShowUnsentOnly] = useState(false);

  const errorMessage = errorState.key ? t(errorState.key) : errorState.fallback;
  const hasRange = Boolean(range.from && range.to);
  const hasEmails = emails.length > 0;

  const displayedEmails = showUnsentOnly
    ? emails.filter(
        (email) => email[COLUMN_NAMES.sentToUser] === false || email[COLUMN_NAMES.sentToClient] === false
      )
    : emails;

  const handleLoadError = (err) => {
    const serverMessage = String(getErrorMessage(err, '') || '').trim().toLowerCase();
    const errorKey = SERVER_ERROR_MESSAGE_KEYS[serverMessage];

    setErrorState({
      key: errorKey || TRANSLATION_KEYS.somethingWentWrong,
      fallback: '',
    });
  };

  const loadReportByRange = async () => {
    if (!fromDate || !toDate) {
      setErrorState({ key: TRANSLATION_KEYS.selectFromToDates, fallback: '' });
      return;
    }

    if (!isValidDateRange(fromDate, toDate)) {
      setErrorState({ key: TRANSLATION_KEYS.invalidRange, fallback: '' });
      return;
    }

    setIsLoading(true);
    setErrorState(INITIAL_ERROR_STATE);

    try {
      const selectedRange = { from: fromDate, to: toDate };
      const response = await getEmailTrackingReport(selectedRange);
      const reportResult = response?.data?.result || {};

      setRange({
        from: reportResult.from || selectedRange.from,
        to: reportResult.to || selectedRange.to,
      });
      setEmails(reportResult.emails || []);
    } catch (err) {
      handleLoadError(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="email-tracking-container">
      <div>
        <div className="title">
          <Text text={t(TRANSLATION_KEYS.reportTitle)} />
        </div>
      </div>

      <div className="email-tracking-actions">
        <div className="date-range-inputs">
          <DateRangeInput
            id="email-tracking-from-date"
            labelKey={TRANSLATION_KEYS.from}
            value={fromDate}
            onChange={setFromDate}
            max={toDate || undefined}
            t={t}
          />
          <DateRangeInput
            id="email-tracking-to-date"
            labelKey={TRANSLATION_KEYS.to}
            value={toDate}
            onChange={setToDate}
            min={fromDate || undefined}
            t={t}
          />
        </div>

        <button
          type="button"
          className="load-report-button"
          onClick={loadReportByRange}
          disabled={isLoading}
        >
          <Text text={t(TRANSLATION_KEYS.loadReport)} />
        </button>
      </div>

      {hasRange && (
        <div className="selected-range">
          <Text text={buildRangeLabel(range, i18n.language, t)} />
          {hasEmails && (
            <button
              type="button"
              className={`filter-unsent-button${showUnsentOnly ? ' active' : ''}`}
              onClick={() => setShowUnsentOnly((prev) => !prev)}
            >
              <Text text={t(TRANSLATION_KEYS.filterUnsent)} />
            </button>
          )}
        </div>
      )}

      {errorMessage !== '' && (
        <div className="error-message">
          <Text text={errorMessage} />
        </div>
      )}

      {isLoading && <Spinner />}

      {!isLoading && hasEmails && (
        <div className="email-report-content">
          <EmailReportTable emails={displayedEmails} t={t} language={i18n.language} />
        </div>
      )}

      {!isLoading && !hasEmails && hasRange && (
        <div className="no-emails-message">
          <Text text={t(TRANSLATION_KEYS.noEmailsFound)} />
        </div>
      )}
    </div>
  );
};

export default EmailTrackingReport;